import Divider from "components/divider";
import TeamView from "components/team_view";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import ActionAvatars from "pages/conductLegalResearch/fragments/actionAvatars";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getEmployeesByAdminAndOrg, isFollowUpProsecutor } from "services/getEmployees";
import { createLawImplementation, createLawImplementationProsecutorAssignment, createLawImplementationTeam } from "services/lawImplementationService";
import { createLegalResearchRequest } from "services/legalResearchService";
import { createProsecutorAssignment } from "services/prosecutorAssignentService";
import { LEGAL_ADVISORY_REQUEST_STATUS } from "utils/constantValues";
import { useTranslation } from "react-i18next";

interface TeamMember {
    id: number;
    name: string;
    role: string;
    isTeamLeader: boolean;
}

const getInitials = (name: string) => {
    if (!name) return "";
    const parts = name.split(" ");
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }
    return (
        parts[0].charAt(0).toUpperCase() +
        parts[parts.length - 1].charAt(0).toUpperCase()
    );
};

export default function CreateRequest() {
    const { t } = useTranslation();
    const [selectedProsecutor, setSelectedProsecutor] =
        useState<TeamMember | null>(null);
    const [employees, setEmployees] = useState([]);
    const [task, setTask] = useState("");
    const [remark, setRemark] = useState("");
    const { showToast } = useToast();
    const contextData = useAuth();
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [teamId, setTeamId] = useState(0);
    const [teams, setTeams] = useState<any>([]);
    const [teamView, setTeamView] = useState(false);
    const [teamName, setTeamName] = useState("");
    const [validationError, setValidationError] = useState<string | null>(null);
    const navigate = useNavigate();

    const handleAddTeam = () => {
        if (!teamName.trim()) {
            setValidationError(t("PleaseEnterTeamName") || "Please enter a team name.");
            return;
        }
        if (teamMembers.length === 0) {
            setValidationError(t("PleaseAddTeamMemberBeforeCreatingTeam") || "Please add at least one team member before creating a team.");
            return;
        }
        if (
            teamMembers &&
            !teams?.some((team: any) => team.team_name === teamName)
        ) {
            setTeams((prevTeam: any) => [
                ...prevTeam,
                {
                    team_name: teamName,
                    team_members: [...teamMembers]
                }
            ]);
            setTeamMembers([]);
            setTeamName("");
            setSelectedProsecutor(null);
            setValidationError(null);
        } else if (
            teamMembers.some((member) => member.id === selectedProsecutor?.id)
        ) {
            setValidationError(t("ProsecutorAlreadyAdded") || "This prosecutor is already added to the team.");
        } else if (teams?.some((team: any) => team.team_name === teamName)) {
            setValidationError(t("TeamNameAlreadyExists") || "A team with this name already exists.");
        }
    };
    
    const handleRemoveMember = (id: number) => {
        const memberToRemove = teamMembers.find((member) => member.id === id);
        if (
            memberToRemove?.isTeamLeader &&
            teamMembers.filter((m) => m.isTeamLeader).length === 1
        ) {
            setValidationError(
                t("CannotRemoveOnlyTeamLeader") || "Cannot remove the only team leader. Please assign a new team leader first or add another member."
            );
            return;
        }
        setTeamMembers(teamMembers.filter((member) => member.id !== id));
        setValidationError(null);
    };
    
    const handleToggleTeamLeader = (id: number) => {
        setTeamMembers((prevMembers) =>
            prevMembers.map((member) => {
                if (member.id === id) {
                    return { ...member, isTeamLeader: !member.isTeamLeader };
                } else {
                    return { ...member, isTeamLeader: false };
                }
            })
        );
        setValidationError(null);
    };
    
    const handleEdit = () => {
        setTeamMembers(teams[teamId].team_members);
        setTeamName(teams[teamId].team_name);
        setTeams((prevTeam: any) => prevTeam.filter((_: any, index: number) => index !== teamId));
    };
    
    const handleRemoveTeam = () => {
        setTeams((prevTeam: any) => prevTeam.filter((_: any, index: number) => index !== teamId));
    };
    
    const handleAddProsecutor = () => {
        if (!selectedProsecutor) {
            setValidationError(t("PleaseSelectProsecutorFirst") || "Please select a prosecutor first.");
            return;
        }
        if (
            selectedProsecutor &&
            !teamMembers.some((member) => member.id === selectedProsecutor.id)
        ) {
            const newId =
                teamMembers.length > 0
                    ? Math.max(...teamMembers.map((m) => m.id)) + 1
                    : 1;
            setTeamMembers((prevMembers) => [
                ...prevMembers,
                {
                    id: selectedProsecutor.id,
                    name: selectedProsecutor.name,
                    role: "Prosecutor",
                    isTeamLeader: prevMembers.length === 0,
                },
            ]);
            setSelectedProsecutor(null);
            setValidationError(null);
        } else if (
            teamMembers.some((member) => member.id === selectedProsecutor?.id)
        ) {
            setValidationError(t("ProsecutorAlreadyAdded") || "This prosecutor is already added to the team.");
        }
    };
    
    useEffect(() => {
        getEmployeesByAdminAndOrg({
            bearer:
                contextData.token ??
                contextData?.user?.access_token ??
                contextData?.user.user?.access_token,
            org_unit: contextData?.user?.org_unit || "",
            admin_unit: contextData?.user?.admin_unit || "",
        })
            .then((data) => {
                setEmployees(
                    data
                        .filter((emp: any) => isFollowUpProsecutor(emp))
                        .map((emp: any) => ({
                            id: emp.user.id,
                            name: emp.user.first_name,
                            role: emp.roles[0] || "LegalResearchProsecutor",
                            isTeamLeader: false,
                        }))
                );
            })
            .catch((error) => {
                console.error("Error fetching:", error);
                showToast(t("FailedToFetchEmployees") || "Failed to fetch employees.", "error");
            });
    }, [getEmployeesByAdminAndOrg, setEmployees, contextData, showToast, t]);

    const handleSubmit = async () => {
        const currentTeamLeaders = teamMembers.filter(
            (member) => member.isTeamLeader
        );

        setValidationError(null);

        // Validation rules
        if (!task.trim()) {
            setValidationError(t("PleaseEnterAuditTarget") || "Please enter the Audit Target.");
            return;
        }
        if (!remark.trim()) {
            setValidationError(t("PleaseEnterRemark") || "Please enter a Remark.");
            return;
        }
        if (teams.length === 0) {
            setValidationError(t("PleaseAddAtLeastOneTeam") || "Please add at least one team.");
            return;
        }
        for (const team of teams) {
            if (!team.team_members || team.team_members.length === 0) {
                setValidationError(t("TeamMustHaveMembers", { teamName: team.team_name }) || `Team "${team.team_name}" must have at least one member.`);
                return;
            }
            const hasLeader = team.team_members.some((m: any) => m.isTeamLeader);
            if (!hasLeader) {
                setValidationError(t("TeamMustHaveLeader", { teamName: team.team_name }) || `Team "${team.team_name}" must have a team leader.`);
                return;
            }
        }

        try {
            const data = new FormData();
            data.append("audit_target", task);
            data.append("description", remark);
            data.append("registry_number", contextData?.user?.admin_unit || "");
            // Teams are assigned right here, so the request starts with the prosecutors;
            // without this it stays "not_forwarded" and the team never gets the
            // "Upload Identified Law or Sector" action (upload_audit_target rule).
            data.append("forward_status", LEGAL_ADVISORY_REQUEST_STATUS.assigned_to_prosecutor.value);
            const response = await createLawImplementation(data);
            for (const team of teams) {
                // A fresh FormData per team: reusing one appended duplicate
                // team_name/followup keys on every iteration.
                const teamData = new FormData();
                teamData.append("team_name", team.team_name);
                teamData.append("followup", response.id.toString());
                const teamResponse = await createLawImplementationTeam(teamData);
                for (const member of team.team_members) {
                    const prosecutorData = new FormData();
                    prosecutorData.append("followup", response.id.toString());
                    prosecutorData.append("followup_team", teamResponse.id.toString());
                    prosecutorData.append("team_type", "prosecutor");
                    prosecutorData.append("user_name", member.name);
                    prosecutorData.append("user_id", member.id.toString());
                    prosecutorData.append(
                        "role",
                        member.isTeamLeader ? "leader" : "member"
                    );
                    await createLawImplementationProsecutorAssignment(prosecutorData);
                }
            }

            showToast(t("TaskCreatedSuccess") || "Legal research Task created and prosecutors assigned!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToSendRequest") || "Failed to send legal research request.", "error");
        } finally {
            navigate("/law-implementation");
        }
    }

    return (
        <div className="flex-1 bg-gray-100 p-4 md:p-6 overflow-auto">
            <div className="rounded-lg bg-white w-full max-w-5xl mx-auto md:mx-0 p-6 md:p-8 shadow-lg">
                {/* Header Section */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-[#9D0303]">
                        {t("CreateLawImplementationRequest") || "Create Law Implementation Request"}
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {t("CreateRequestDescription") || "Fill in the details below to create a new law implementation follow-up request with team assignments."}
                    </p>
                </div>

                {/* Error Message */}
                {validationError && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {validationError}
                    </div>
                )}

                <form className="space-y-8">
                    {/* Basic Information Section */}
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <svg className="w-5 h-5 mr-2 text-[#9D0303]" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a3 3 0 10-6 0v1h6z" clipRule="evenodd" />
                            </svg>
                            {t("BasicInformation") || "Basic Information"}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="flex flex-col gap-2">
                                <label className="text-[#747171] font-medium">
                                    {t("AuditTarget") || "Audit Target"} <span className="text-red-500">*</span>
                                </label>
                                <input 
                                    value={task} 
                                    onChange={(e) => setTask(e.target.value)} 
                                    className="bg-white p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#9D0303] focus:border-transparent transition-all" 
                                    placeholder={t("AuditTargetPlaceholder") || "Enter the law or government sector to identify"} 
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-[#747171] font-medium">
                                    {t("Description") || "Description"} <span className="text-red-500">*</span>
                                </label>
                                <textarea 
                                    value={remark} 
                                    onChange={(e) => setRemark(e.target.value)} 
                                    className="bg-white p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#9D0303] focus:border-transparent transition-all h-24" 
                                    placeholder={t("DescriptionPlaceholder") || "Provide detailed description of the request..."} 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Team Setup Section */}
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <svg className="w-5 h-5 mr-2 text-[#9D0303]" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                            </svg>
                            {t("TeamSetup") || "Team Setup"} <span className="text-red-500">*</span>
                        </h2>
                        
                        {/* Team Name Input */}
                        <div className="mb-4">
                            <label className="text-[#747171] font-medium block mb-2">{t("TeamName") || "Team Name"}</label>
                            <div className="flex gap-3">
                                <input 
                                    value={teamName} 
                                    onChange={(e) => setTeamName(e.target.value)} 
                                    className="flex-1 bg-white p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#9D0303] focus:border-transparent transition-all" 
                                    placeholder={t("TeamNamePlaceholder") || "Enter team name (e.g., Team Alpha)"} 
                                />
                                <button
                                    type="button"
                                    onClick={handleAddTeam}
                                    className="px-6 py-3 bg-[#9D0303] text-white rounded-lg hover:bg-[#7a0202] transition-colors font-medium flex items-center"
                                >
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    {t("CreateTeam") || "Create Team"}
                                </button>
                            </div>
                        </div>

                        {/* Add Team Members */}
                        <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200">
                            <h3 className="font-medium text-gray-700 mb-3">{t("AddTeamMembers") || "Add Team Members"}</h3>
                            <div className="flex gap-3">
                                <div className="relative flex-1">
                                    <select
                                        id="select-prosecutor"
                                        className="appearance-none w-full p-3 border border-gray-300 rounded-md bg-white pr-10 focus:outline-none focus:ring-2 focus:ring-[#9D0303] focus:border-transparent"
                                        value={selectedProsecutor?.id || ""}
                                        onChange={(e) => {
                                            const selectedId = e.target.value;
                                            const found = employees.find(
                                                (employee: any) => String(employee.id) === selectedId
                                            );
                                            setSelectedProsecutor(found ?? null);
                                        }}
                                    >
                                        <option value="">{t("SelectProsecutor") || "Select a prosecutor..."}</option>
                                        {employees.map((emp: any) => (
                                            <option key={emp.id} value={emp.id}>
                                                {emp.name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-700">
                                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleAddProsecutor}
                                    className="px-4 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                                >
                                    {t("Add") || "Add"}
                                </button>
                            </div>
                        </div>

                        {/* Current Team Members */}
                        <div className="mb-6">
                            <h3 className="font-medium text-gray-700 mb-3">
                                {t("CurrentTeamMembers", { count: teamMembers.length }) || `Current Team Members (${teamMembers.length})`}
                            </h3>
                            <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
                                {teamMembers.length === 0 ? (
                                    <p className="text-gray-400 text-sm italic py-4 text-center bg-white rounded-lg border border-dashed border-gray-300">
                                        {t("NoTeamMembersAddedYet") || "No team members added yet. Select a prosecutor above and click Add."}
                                    </p>
                                ) : (
                                    teamMembers.map((member) => (
                                        <div
                                            key={member.id}
                                            className="flex items-center bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                                        >
                                            <div className="w-10 h-10 rounded-full mr-3 bg-[#9D0303] text-white flex items-center justify-center font-bold text-sm">
                                                {getInitials(member.name)}
                                            </div>

                                            <div className="flex-grow">
                                                <p className="font-semibold text-gray-800">
                                                    {member.name}
                                                </p>
                                                <p className="text-sm text-gray-500">{member.role}</p>
                                            </div>
                                            <div className="flex items-center space-x-4">
                                                <label className="flex items-center space-x-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        id={`team-leader-${member.id}`}
                                                        checked={member.isTeamLeader}
                                                        onChange={() => handleToggleTeamLeader(member.id)}
                                                        className="accent-[#9D0303] form-checkbox h-4 w-4"
                                                    />
                                                    <span className="text-sm text-gray-700">{t("TeamLeader") || "Team Leader"}</span>
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveMember(member.id)}
                                                    className="text-gray-400 hover:text-red-500 p-1"
                                                >
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Created Teams Summary */}
                        {teams && teams.length > 0 && (
                            <div className="mt-6">
                                <h3 className="font-medium text-gray-700 mb-3">
                                    {t("CreatedTeams", { count: teams.length }) || `Created Teams (${teams.length})`}
                                </h3>
                                <div className="space-y-3">
                                    {teams.map((team: any, index: number) => (
                                        <div key={index} className="flex items-center bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                            <div className="flex-1">
                                                <p className="font-semibold text-gray-800">{team.team_name}</p>
                                                <p className="text-sm text-gray-500">
                                                    {t("MembersCount", { count: team.team_members.length }) || `${team.team_members.length} member(s)`}
                                                </p>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {(contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") || contextData?.user?.roles?.some((p) => p === "ZonalLIF")) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => { handleEdit(); setTeamId(index); }}
                                                        className="p-2 text-gray-500 hover:text-[#9D0303] hover:bg-gray-100 rounded-lg"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => { setTeamView(!teamView); setTeamId(index); }}
                                                    className="p-2 text-gray-500 hover:text-[#9D0303] hover:bg-gray-100 rounded-lg"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setTeamId(index); handleRemoveTeam(); }}
                                                    className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 rounded-lg"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {teams && teamView && (
                            <TeamView teamMembers={teams?.[teamId]?.team_members} onClose={() => setTeamView(!teamView)} />
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                        <button 
                            type="button" 
                            onClick={() => navigate("/law-implementation")} 
                            className="px-8 py-3 border border-gray-800 text-gray-800 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                        >
                            {t("Cancel") || "Cancel"}
                        </button>
                        <button 
                            type="button" 
                            onClick={handleSubmit} 
                            className="px-8 py-3 bg-[#212121] text-white rounded-lg hover:bg-gray-800 transition-colors font-medium flex items-center"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {t("SubmitRequest") || "Submit Request"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}